import React, { useState, useEffect } from 'react';
import API_URL from '../config';
import './AdminPage.css';

const AdminPage = () => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // Navigation
  const [activeTab, setActiveTab] = useState('dashboard');

  // Dashboard stats
  const [stats, setStats] = useState({
    totalRegistrations: 0,
    totalBookings: 0,
    validatedBookings: 0,
    paidNotValidated: 0,
    bookingsByHotel: {},
    registrationsByJobTitle: {}
  });

  // Registrations state
  const [registrations, setRegistrations] = useState([]);
  const [regPage, setRegPage] = useState(1);
  const [regTotalPages, setRegTotalPages] = useState(1);
  const [regSearch, setRegSearch] = useState('');
  const [regJobTitle, setRegJobTitle] = useState('');
  const [regSortBy, setRegSortBy] = useState('created_at');
  const [regSortOrder, setRegSortOrder] = useState('desc');

  // Joined (GA + Hotel) state
  const [joinedData, setJoinedData] = useState([]);
  const [joinedTotal, setJoinedTotal] = useState(0);

  // Bookings state
  const [bookings, setBookings] = useState([]);
  const [bookPage, setBookPage] = useState(1);
  const [bookTotalPages, setBookTotalPages] = useState(1);
  const [bookSearch, setBookSearch] = useState('');
  const [bookHotelName, setBookHotelName] = useState('');
  const [bookPaymentStatus, setBookPaymentStatus] = useState('');
  const [bookReservationStatus, setBookReservationStatus] = useState('');
  const [bookValidated, setBookValidated] = useState('');
  const [bookPaidNotValidated, setBookPaidNotValidated] = useState(false);
  const [bookSortBy, setBookSortBy] = useState('created_at');
  const [bookSortOrder, setBookSortOrder] = useState('desc');

  // Modal state
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [editData, setEditData] = useState({
    paymentStatus: '',
    reservationStatus: '',
    roomType: '',
    adminNotes: ''
  });

  // Check auth on mount
  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      if (activeTab === 'dashboard') {
        fetchStats();
      } else if (activeTab === 'registrations') {
        fetchRegistrations();
      } else if (activeTab === 'bookings') {
        fetchBookings();
      } else if (activeTab === 'joined') {
        fetchJoined();
      }
    }
  }, [isAuthenticated, activeTab]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('adminToken');
        setToken(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Token verification error:', error);
      localStorage.removeItem('adminToken');
      setToken(null);
      setIsAuthenticated(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    try {
      const response = await fetch(`${API_URL}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (response.ok && data.access_token) {
        localStorage.setItem('adminToken', data.access_token);
        setToken(data.access_token);
        setIsAuthenticated(true);
        setPassword('');
      } else {
        setLoginError(data.error || 'Login failed');
      }
    } catch (error) {
      setLoginError('Connection error');
      console.error('Login error:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchRegistrations = async () => {
    try {
      const params = new URLSearchParams({
        page: regPage,
        per_page: 50,
        search: regSearch,
        jobTitle: regJobTitle,
        sortBy: regSortBy,
        sortOrder: regSortOrder
      });

      const response = await fetch(`${API_URL}/admin/registrations?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      setRegistrations(data.registrations || []);
      setRegTotalPages(data.pages || 1);
    } catch (error) {
      console.error('Error fetching registrations:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      const params = new URLSearchParams({
        page: bookPage,
        per_page: 50,
        search: bookSearch,
        hotelName: bookHotelName,
        paymentStatus: bookPaymentStatus,
        reservationStatus: bookReservationStatus,
        validated: bookValidated,
        paidNotValidated: bookPaidNotValidated ? 'true' : '',
        sortBy: bookSortBy,
        sortOrder: bookSortOrder
      });

      const response = await fetch(`${API_URL}/admin/hotel-bookings?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      setBookings(data.bookings || []);
      setBookTotalPages(data.pages || 1);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const fetchJoined = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/joined`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setJoinedData(data.joined || []);
      setJoinedTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching joined data:', error);
    }
  };

  const handleExportRegistrations = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/registrations/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `registrations_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting registrations:', error);
    }
  };

  const handleExportBookings = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/hotel-bookings/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hotel_bookings_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting bookings:', error);
    }
  };

  const openValidateModal = (booking) => {
    setSelectedBooking(booking);
    setAdminNotes('');
    setShowValidateModal(true);
  };

  const openEditModal = (booking) => {
    setSelectedBooking(booking);
    setEditData({
      paymentStatus: booking.payment_status,
      reservationStatus: booking.reservation_status,
      roomType: booking.room_type || '',
      adminNotes: booking.admin_notes || ''
    });
    setShowEditModal(true);
  };

  const openDetailsModal = (booking) => {
    setSelectedBooking(booking);
    setShowDetailsModal(true);
  };

  const handleValidateBooking = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/hotel-bookings/${selectedBooking.id}/validate`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ admin_notes: adminNotes })
      });

      if (response.ok) {
        setShowValidateModal(false);
        fetchBookings();
        fetchStats();
        alert('Booking validated successfully!');
      } else {
        const data = await response.json();
        alert(data.error || 'Validation failed');
      }
    } catch (error) {
      console.error('Error validating booking:', error);
      alert('Validation error');
    }
  };

  const handleEditBooking = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/hotel-bookings/${selectedBooking.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          payment_status: editData.paymentStatus,
          reservation_status: editData.reservationStatus,
          room_type: editData.roomType,
          admin_notes: editData.adminNotes
        })
      });

      if (response.ok) {
        setShowEditModal(false);
        fetchBookings();
        fetchStats();
        alert('Booking updated successfully!');
      } else {
        const data = await response.json();
        alert(data.error || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      alert('Update error');
    }
  };

  // Refetch when filters change — debounce 400ms to avoid rate-limit errors on keystrokes
  useEffect(() => {
    if (isAuthenticated && activeTab === 'registrations') {
      const timer = setTimeout(fetchRegistrations, 400);
      return () => clearTimeout(timer);
    }
  }, [regPage, regSearch, regJobTitle, regSortBy, regSortOrder]);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'bookings') {
      const timer = setTimeout(fetchBookings, 400);
      return () => clearTimeout(timer);
    }
  }, [bookPage, bookSearch, bookHotelName, bookPaymentStatus, bookReservationStatus, bookValidated, bookPaidNotValidated, bookSortBy, bookSortOrder]);

  // Login form
  if (!isAuthenticated) {
    return (
      <div className="admin-page">
        <div className="admin-login">
          <h1>Admin Login</h1>
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {loginError && <div className="error-message">{loginError}</div>}
            <button type="submit" className="btn-login">Login</button>
          </form>
        </div>
      </div>
    );
  }

  // Main admin interface
  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-header">
          <h1>CISAC GA 2026 - Admin Dashboard</h1>
          <button onClick={handleLogout} className="btn-logout">Logout</button>
        </div>

        <div className="admin-tabs">
          <button
            className={`tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>
          <button
            className={`tab-button ${activeTab === 'registrations' ? 'active' : ''}`}
            onClick={() => setActiveTab('registrations')}
          >
            Registrations
          </button>
          <button
            className={`tab-button ${activeTab === 'bookings' ? 'active' : ''}`}
            onClick={() => setActiveTab('bookings')}
          >
            Hotel Bookings
          </button>
          <button
            className={`tab-button ${activeTab === 'joined' ? 'active' : ''}`}
            onClick={() => setActiveTab('joined')}
          >
            GA + Hotel
          </button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="dashboard">
            <div className="stat-card">
              <h3>Total Registrations</h3>
              <div className="stat-value">{stats.totalRegistrations}</div>
            </div>
            <div className="stat-card">
              <h3>Total Bookings</h3>
              <div className="stat-value">{stats.totalBookings}</div>
            </div>
            <div className="stat-card">
              <h3>Validated Bookings</h3>
              <div className="stat-value">{stats.validatedBookings}</div>
            </div>
            <div className="stat-card danger">
              <h3>Paid Not Validated</h3>
              <div className="stat-value">{stats.paidNotValidated}</div>
            </div>

            <div className="stat-section">
              <h3>Bookings by Hotel</h3>
              <div className="stat-list">
                {Object.entries(stats.bookingsByHotel || {}).map(([hotel, count]) => (
                  <div key={hotel} className="stat-item">
                    <span>{hotel}</span>
                    <span className="stat-count">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="stat-section">
              <h3>Registrations by Job Title</h3>
              <div className="stat-list">
                {Object.entries(stats.registrationsByJobTitle || {}).map(([title, count]) => (
                  <div key={title} className="stat-item">
                    <span>{title}</span>
                    <span className="stat-count">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Registrations Tab */}
        {activeTab === 'registrations' && (
          <div>
            <div className="filters-bar">
              <input
                type="text"
                className="filter-input"
                placeholder="Search by name or email..."
                value={regSearch}
                onChange={(e) => { setRegSearch(e.target.value); setRegPage(1); }}
              />
              <select
                className="filter-select"
                value={regJobTitle}
                onChange={(e) => { setRegJobTitle(e.target.value); setRegPage(1); }}
              >
                <option value="">All Job Titles</option>
                <option value="society_colleague">Society Colleague</option>
                <option value="creator">Creator</option>
                <option value="other">Other</option>
              </select>
              <select
                className="filter-select"
                value={regSortBy}
                onChange={(e) => setRegSortBy(e.target.value)}
              >
                <option value="created_at">Date</option>
                <option value="first_name">Name</option>
                <option value="email">Email</option>
              </select>
              <select
                className="filter-select"
                value={regSortOrder}
                onChange={(e) => setRegSortOrder(e.target.value)}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
              <button onClick={handleExportRegistrations} className="btn-export">
                Export XLSX
              </button>
            </div>

            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Job Title</th>
                    <th>Society</th>
                    <th>Position</th>
                    <th>Hotel Bookings</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((reg) => (
                    <tr key={reg.id}>
                      <td>{reg.id}</td>
                      <td>{reg.first_name} {reg.last_name}</td>
                      <td>{reg.email}</td>
                      <td>{reg.job_title}</td>
                      <td>{reg.society}</td>
                      <td>{reg.position}</td>
                      <td>{reg.hotel_bookings_count || 0}</td>
                      <td>{new Date(reg.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                className="page-button"
                onClick={() => setRegPage(p => Math.max(1, p - 1))}
                disabled={regPage === 1}
              >
                Previous
              </button>
              {[...Array(regTotalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  className={`page-button ${regPage === i + 1 ? 'active' : ''}`}
                  onClick={() => setRegPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button
                className="page-button"
                onClick={() => setRegPage(p => Math.min(regTotalPages, p + 1))}
                disabled={regPage === regTotalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div>
            <div className="filters-bar">
              <input
                type="text"
                className="filter-input"
                placeholder="Search by name or email..."
                value={bookSearch}
                onChange={(e) => { setBookSearch(e.target.value); setBookPage(1); }}
              />
              <select
                className="filter-select"
                value={bookHotelName}
                onChange={(e) => { setBookHotelName(e.target.value); setBookPage(1); }}
              >
                <option value="">All Hotels</option>
                <option value="Hôtel de l'Arcade">Hôtel de l'Arcade</option>
                <option value="Hôtel du Triangle d'Or">Hôtel du Triangle d'Or</option>
                <option value="Hôtel Bedford">Hôtel Bedford</option>
              </select>
              <select
                className="filter-select"
                value={bookPaymentStatus}
                onChange={(e) => { setBookPaymentStatus(e.target.value); setBookPage(1); }}
              >
                <option value="">All Payment Status</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select
                className="filter-select"
                value={bookReservationStatus}
                onChange={(e) => { setBookReservationStatus(e.target.value); setBookPage(1); }}
              >
                <option value="">All Reservation Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={bookValidated === 'true'}
                  onChange={(e) => { setBookValidated(e.target.checked ? 'true' : ''); setBookPage(1); }}
                />
                Validated Only
              </label>
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={bookPaidNotValidated}
                  onChange={(e) => { setBookPaidNotValidated(e.target.checked); setBookPage(1); }}
                />
                Paid Not Validated
              </label>
              <select
                className="filter-select"
                value={bookSortBy}
                onChange={(e) => setBookSortBy(e.target.value)}
              >
                <option value="created_at">Date</option>
                <option value="check_in_date">Check-in</option>
                <option value="hotel_name">Hotel</option>
                <option value="guest_first_name">Guest</option>
              </select>
              <select
                className="filter-select"
                value={bookSortOrder}
                onChange={(e) => setBookSortOrder(e.target.value)}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
              <button onClick={handleExportBookings} className="btn-export">
                Export XLSX
              </button>
            </div>

            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Guest Name</th>
                    <th>Email</th>
                    <th>Hotel</th>
                    <th>Room Type</th>
                    <th>Check-in</th>
                    <th>Check-out</th>
                    <th>Guests</th>
                    <th>Payment</th>
                    <th>Reservation</th>
                    <th>Validated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking.id}>
                      <td>{booking.id}</td>
                      <td>{booking.guest_first_name} {booking.guest_last_name}</td>
                      <td>{booking.guest_email}</td>
                      <td>{booking.hotel_name}</td>
                      <td>{booking.room_type || 'N/A'}</td>
                      <td>{new Date(booking.check_in_date).toLocaleDateString()}</td>
                      <td>{new Date(booking.check_out_date).toLocaleDateString()}</td>
                      <td>{booking.number_of_guests}</td>
                      <td>
                        <span className={`badge ${booking.payment_status}`}>
                          {booking.payment_status}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${booking.reservation_status}`}>
                          {booking.reservation_status}
                        </span>
                      </td>
                      <td>
                        {booking.validated ? (
                          <span className="validation-icon success">YES</span>
                        ) : (
                          <span className="validation-icon error">NO</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn-info"
                          onClick={() => openDetailsModal(booking)}
                        >
                          Details
                        </button>
                        {!booking.validated && (
                          <button
                            className="btn-validate"
                            onClick={() => openValidateModal(booking)}
                          >
                            Validate
                          </button>
                        )}
                        <button
                          className="btn-edit"
                          onClick={() => openEditModal(booking)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                className="page-button"
                onClick={() => setBookPage(p => Math.max(1, p - 1))}
                disabled={bookPage === 1}
              >
                Previous
              </button>
              {[...Array(bookTotalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  className={`page-button ${bookPage === i + 1 ? 'active' : ''}`}
                  onClick={() => setBookPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button
                className="page-button"
                onClick={() => setBookPage(p => Math.min(bookTotalPages, p + 1))}
                disabled={bookPage === bookTotalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
        {/* GA + Hotel Tab */}
        {activeTab === 'joined' && (
          <div>
            <div className="filters-bar">
              <span className="filter-info">
                {joinedTotal} GA registrant{joinedTotal !== 1 ? 's' : ''} with a hotel booking
              </span>
              <button onClick={fetchJoined} className="btn-export">Refresh</button>
            </div>

            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Job Title</th>
                    <th>Society</th>
                    <th>Hotel</th>
                    <th>Room</th>
                    <th>Check-in</th>
                    <th>Check-out</th>
                    <th>Status</th>
                    <th>Validated</th>
                  </tr>
                </thead>
                <tbody>
                  {joinedData.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.registration.first_name} {item.registration.last_name}</td>
                      <td>{item.registration.email}</td>
                      <td>{item.registration.job_title}</td>
                      <td>{item.registration.society || '—'}</td>
                      <td>{item.booking.hotel_name}</td>
                      <td>{item.booking.room_type || 'N/A'}</td>
                      <td>{new Date(item.booking.check_in_date).toLocaleDateString()}</td>
                      <td>{new Date(item.booking.check_out_date).toLocaleDateString()}</td>
                      <td>
                        <span className={`badge ${item.booking.reservation_status}`}>
                          {item.booking.reservation_status}
                        </span>
                      </td>
                      <td>
                        {item.booking.validated ? (
                          <span className="validation-icon success">YES</span>
                        ) : (
                          <span className="validation-icon error">NO</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Validate Modal */}
      {showValidateModal && (
        <div className="modal-overlay" onClick={() => setShowValidateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Validate Booking</h2>
            <p>Guest: {selectedBooking.guest_first_name} {selectedBooking.guest_last_name}</p>
            <p>Hotel: {selectedBooking.hotel_name}</p>
            <div className="form-group">
              <label>Admin Notes</label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows="4"
                placeholder="Add validation notes..."
              />
            </div>
            <div className="modal-buttons">
              <button onClick={handleValidateBooking} className="btn-validate">
                Confirm Validation
              </button>
              <button onClick={() => setShowValidateModal(false)} className="btn-cancel">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Booking</h2>
            <div className="form-group">
              <label>Payment Status</label>
              <select
                value={editData.paymentStatus}
                onChange={(e) => setEditData({ ...editData, paymentStatus: e.target.value })}
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="form-group">
              <label>Reservation Status</label>
              <select
                value={editData.reservationStatus}
                onChange={(e) => setEditData({ ...editData, reservationStatus: e.target.value })}
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="form-group">
              <label>Room Type</label>
              <input
                type="text"
                value={editData.roomType}
                onChange={(e) => setEditData({ ...editData, roomType: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Admin Notes</label>
              <textarea
                value={editData.adminNotes}
                onChange={(e) => setEditData({ ...editData, adminNotes: e.target.value })}
                rows="4"
              />
            </div>
            <div className="modal-buttons">
              <button onClick={handleEditBooking} className="btn-edit">
                Save Changes
              </button>
              <button onClick={() => setShowEditModal(false)} className="btn-cancel">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedBooking && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <h2>Booking Details #{selectedBooking.id}</h2>

            <div className="details-section">
              <h3>Guest Information</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <span className="detail-label">Name:</span>
                  <span className="detail-value">{selectedBooking.guest_first_name} {selectedBooking.guest_last_name}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{selectedBooking.guest_email}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Registration ID:</span>
                  <span className="detail-value">{selectedBooking.registration_id}</span>
                </div>
              </div>
            </div>

            <div className="details-section">
              <h3>Hotel & Booking Information</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <span className="detail-label">Hotel:</span>
                  <span className="detail-value">{selectedBooking.hotel_name}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Room Type:</span>
                  <span className="detail-value">{selectedBooking.room_type || 'Not specified'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Check-in:</span>
                  <span className="detail-value">{new Date(selectedBooking.check_in_date).toLocaleDateString()}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Check-out:</span>
                  <span className="detail-value">{new Date(selectedBooking.check_out_date).toLocaleDateString()}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Number of Guests:</span>
                  <span className="detail-value">{selectedBooking.number_of_guests}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Duration:</span>
                  <span className="detail-value">
                    {Math.ceil((new Date(selectedBooking.check_out_date) - new Date(selectedBooking.check_in_date)) / (1000 * 60 * 60 * 24))} nights
                  </span>
                </div>
              </div>
            </div>

            {selectedBooking.special_requests && (
              <div className="details-section">
                <h3>Special Requests</h3>
                <p className="detail-text">{selectedBooking.special_requests}</p>
              </div>
            )}

            <div className="details-section">
              <h3>Status Information</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <span className="detail-label">Payment Status:</span>
                  <span className={`badge ${selectedBooking.payment_status}`}>
                    {selectedBooking.payment_status}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Reservation Status:</span>
                  <span className={`badge ${selectedBooking.reservation_status}`}>
                    {selectedBooking.reservation_status}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Validated:</span>
                  <span className={selectedBooking.validated ? 'validation-icon success' : 'validation-icon error'}>
                    {selectedBooking.validated ? 'YES' : 'NO'}
                  </span>
                </div>
                {selectedBooking.validated_at && (
                  <div className="detail-item">
                    <span className="detail-label">Validated At:</span>
                    <span className="detail-value">{new Date(selectedBooking.validated_at).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>

            {selectedBooking.admin_notes && (
              <div className="details-section">
                <h3>Admin Notes</h3>
                <p className="detail-text admin-notes">{selectedBooking.admin_notes}</p>
              </div>
            )}

            <div className="details-section">
              <h3>Timestamps</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <span className="detail-label">Created At:</span>
                  <span className="detail-value">{new Date(selectedBooking.created_at).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Last Updated:</span>
                  <span className="detail-value">{new Date(selectedBooking.updated_at).toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="modal-buttons">
              <button onClick={() => setShowDetailsModal(false)} className="btn-cancel">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
